/**
 * Workforce Dashboard Page
 *
 * Overview dashboard for workforce management showing:
 * - Key metrics (candidates, certifications, roster)
 * - Pipeline summary
 * - Certification expiration alerts
 * - Recent activity
 *
 * Note: This page is rendered inline in App.tsx using the admin dashboard's
 * authentication system (access_token in localStorage).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Award,
  BookOpen,
  GraduationCap,
  ClipboardList,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Clock,
  Calendar,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface DashboardStats {
  total_candidates: number;
  active_candidates: number;
  enrolled_candidates: number;
  total_roles: number;
  open_positions: number;
  active_certifications: number;
  expiring_soon: number;
  expired: number;
  active_roster: number;
  active_mentoring_programs: number;
}

interface ExpiringCertification {
  id: number;
  user_name: string;
  certification_name: string;
  expiration_date: string;
  days_until_expiration: number;
}

interface RecentCandidate {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  pipeline_stage: string;
  created_at: string;
}

export default function WorkforceDashboardPage() {
  const { t } = useAppTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [expiringCerts, setExpiringCerts] = useState<ExpiringCertification[]>([]);
  const [recentCandidates, setRecentCandidates] = useState<RecentCandidate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = getAuthToken();
      if (!token) {
        console.warn('No auth token found for workforce dashboard');
        setLoading(false);
        return;
      }

      try {
        // Fetch dashboard stats
        const [statsRes, certsRes, candidatesRes] = await Promise.all([
          authFetch(getWeaveinApiUrl('/api/workforce/dashboard/stats')),
          authFetch(getWeaveinApiUrl('/api/workforce/certifications/expiring?days=30')),
          authFetch(getWeaveinApiUrl('/api/workforce/candidates?limit=5&sort=-created_at')),
        ]);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        if (certsRes.ok) {
          const certsData = await certsRes.json();
          setExpiringCerts((certsData.items || []).slice(0, 5));
        }

        if (candidatesRes.ok) {
          const candidatesData = await candidatesRes.json();
          setRecentCandidates(candidatesData.items?.slice(0, 5) || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    {
      title: t('workforce.dashboard.stats.activeCandidates', 'Active Candidates'),
      value: stats?.active_candidates || 0,
      icon: UserPlus,
      color: 'bg-blue-100 text-blue-600',
      link: '/workforce/candidates',
    },
    {
      title: t('workforce.dashboard.stats.openPositions', 'Open Positions'),
      value: stats?.open_positions || 0,
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      link: '/workforce/roles',
    },
    {
      title: t('workforce.dashboard.stats.activeCertifications', 'Active Certifications'),
      value: stats?.active_certifications || 0,
      icon: Award,
      color: 'bg-emerald-100 text-emerald-600',
      link: '/workforce/certifications',
    },
    {
      title: t('workforce.dashboard.stats.workforceRoster', 'Workforce Roster'),
      value: stats?.active_roster || 0,
      icon: ClipboardList,
      color: 'bg-orange-100 text-orange-600',
      link: '/workforce/roster',
    },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      prospect: 'bg-paper-2 text-ink-700',
      screening: 'bg-blue-100 text-blue-700',
      interview: 'bg-purple-100 text-purple-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      enrolled: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-paper-2 text-ink-700';
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Overview"
        title={t('workforce.dashboard.title', 'Workforce Dashboard')}
        description={t('workforce.dashboard.description', 'Overview of recruitment, training, and workforce deployment')}
      />
      <PageContainer>
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.title}
                to={stat.link}
                className="bg-surface rounded-xl p-6 border border-v2border hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-ink-500">{stat.title}</p>
                    <p className="font-display text-3xl font-semibold text-ink mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Alerts Section */}
        {(stats?.expiring_soon || 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {stats?.expiring_soon} {t('workforce.dashboard.alerts.certificationsExpiring', 'certification(s) expiring soon')}
                </p>
                <p className="text-sm text-amber-600">{t('workforce.dashboard.alerts.reviewReminders', 'Review and send renewal reminders')}</p>
              </div>
              <Link
                to="/workforce/certifications?filter=expiring"
                className="ml-auto text-amber-700 hover:text-amber-800 font-medium text-sm flex items-center"
              >
                {t('workforce.dashboard.viewAll', 'View All')} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expiring Certifications */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">{t('workforce.dashboard.expiringCerts.title', 'Expiring Certifications')}</h2>
              <Link
                to="/workforce/certifications"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('workforce.dashboard.viewAll', 'View All')}
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {loading ? (
                <div className="p-6 text-center text-ink-500">{t('workforce.dashboard.loading', 'Loading...')}</div>
              ) : expiringCerts.length === 0 ? (
                <div className="p-6 text-center">
                  <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-ink-500">{t('workforce.dashboard.expiringCerts.noneExpiring', 'No certifications expiring soon')}</p>
                </div>
              ) : (
                expiringCerts.map((cert) => (
                  <div key={cert.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-ink">{cert.user_name}</p>
                      <p className="text-sm text-ink-500">{cert.certification_name}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        cert.days_until_expiration <= 7 ? 'text-red-600' :
                        cert.days_until_expiration <= 14 ? 'text-amber-600' : 'text-ink-500'
                      }`}>
                        {cert.days_until_expiration} {t('workforce.dashboard.expiringCerts.days', 'days')}
                      </p>
                      <p className="text-xs text-ink-400">
                        {new Date(cert.expiration_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Candidates */}
          <div className="bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">{t('workforce.dashboard.recentCandidates.title', 'Recent Candidates')}</h2>
              <Link
                to="/workforce/candidates"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('workforce.dashboard.viewAll', 'View All')}
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {loading ? (
                <div className="p-6 text-center text-ink-500">{t('workforce.dashboard.loading', 'Loading...')}</div>
              ) : recentCandidates.length === 0 ? (
                <div className="p-6 text-center">
                  <UserPlus className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                  <p className="text-ink-500">{t('workforce.dashboard.recentCandidates.noCandidates', 'No candidates yet')}</p>
                  <Link
                    to="/workforce/candidates/new"
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium mt-2 inline-block"
                  >
                    {t('workforce.dashboard.recentCandidates.addFirst', 'Add First Candidate')}
                  </Link>
                </div>
              ) : (
                recentCandidates.map((candidate) => (
                  <Link
                    key={candidate.id}
                    to={`/workforce/candidates/${candidate.id}`}
                    className="px-6 py-4 flex items-center justify-between hover:bg-paper"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-paper-2 flex items-center justify-center">
                        <span className="text-ink-500 font-medium">
                          {candidate.first_name[0]}{candidate.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-ink">
                          {candidate.first_name} {candidate.last_name}
                        </p>
                        <p className="text-sm text-ink-500">{candidate.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                      {candidate.pipeline_stage || candidate.status}
                    </span>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-surface rounded-xl border border-v2border p-6">
          <h2 className="font-semibold text-ink mb-4">{t('workforce.dashboard.quickActions.title', 'Quick Actions')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/workforce/candidates/new"
              className="flex flex-col items-center p-4 rounded-lg border border-v2border hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <UserPlus className="w-8 h-8 text-emerald-600 mb-2" />
              <span className="text-sm font-medium text-ink-700">{t('workforce.dashboard.quickActions.addCandidate', 'Add Candidate')}</span>
            </Link>
            <Link
              to="/workforce/roles/new"
              className="flex flex-col items-center p-4 rounded-lg border border-v2border hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <Users className="w-8 h-8 text-emerald-600 mb-2" />
              <span className="text-sm font-medium text-ink-700">{t('workforce.dashboard.quickActions.createRole', 'Create Role')}</span>
            </Link>
            <Link
              to="/workforce/curricula/new"
              className="flex flex-col items-center p-4 rounded-lg border border-v2border hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <BookOpen className="w-8 h-8 text-emerald-600 mb-2" />
              <span className="text-sm font-medium text-ink-700">{t('workforce.dashboard.quickActions.newCurriculum', 'New Curriculum')}</span>
            </Link>
            <Link
              to="/workforce/skills-gap"
              className="flex flex-col items-center p-4 rounded-lg border border-v2border hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <TrendingUp className="w-8 h-8 text-emerald-600 mb-2" />
              <span className="text-sm font-medium text-ink-700">{t('workforce.dashboard.quickActions.runAnalysis', 'Run Analysis')}</span>
            </Link>
          </div>
        </div>
      </div>
      </PageContainer>
    </div>
  );
}
